import { AfterViewInit, Component, OnDestroy, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription, map, of } from 'rxjs';
import {
  Filters,
  FnCaricamentoDati,
  SortBy,
} from 'src/app/components/lista-tabellare/classes/lista-tabellare-data-source';
import {
  BottoniListaEvent,
  Colonna,
  Filtri,
  ListaTabellareComponent,
} from 'src/app/components/lista-tabellare/lista-tabellare.component';
import { SpinnerOverlayComponent } from 'src/app/components/spinner-overlay/spinner-overlay.component';
import { Azienda, AziendaService } from 'src/app/services/azienda/azienda.service';
import { DoppiaMaterialitaService } from 'src/app/services/doppia-materialita/doppia-materialita.service';
import { LicenzeService } from 'src/app/services/licenze/licenze.service';
import { SettoriService } from 'src/app/services/settori/settori.service';
import { UtenteService } from 'src/app/services/utente/utente.service';
import { UtilityService } from 'src/app/services/utility/utility.service';
import { DialogCreaDoppiaMaterialitaComponent } from './dialog/dialog-crea-doppia-materialita/dialog-crea-doppia-materialita.component';

@Component({
  selector: 'app-doppia-materialita',
  standalone: false,
  templateUrl: './doppia-materialita.component.html',
  styleUrl: './doppia-materialita.component.scss',
})
export class DoppiaMaterialitaComponent implements AfterViewInit, OnDestroy {
  @ViewChild('tabella') tabella!: ListaTabellareComponent;
  @ViewChild(SpinnerOverlayComponent) spinnerOver!: SpinnerOverlayComponent;

  private _subAziendaCorrente: Subscription | undefined;

  private azienda: Azienda | undefined = undefined;

  public filtri: Filtri[] = [
    {
      titolo: 'Anno',
      forControlName: 'anno',
      input: 'text',
    },
    {
      forControlName: 'settore',
      input: 'multiple-option',
      titolo: 'Settore',
      fnDatiOption: () =>
        this.settoriService.getSettori(0, 1000).pipe(map((value) => value?.content || [])),
      descValueOption: 'titolo',
      idValueOption: 'id',
      riempi: true,
    },
    {
      forControlName: 'stato',
      input: 'multiple-option',
      titolo: 'Stato',
      fnDatiOption: () =>
        of([
          {
            valore: 'PUBBLICATO',
            desc: 'Completata',
          },
          {
            valore: 'BOZZA',
            desc: 'Non completata',
          },
        ]),
      descValueOption: 'desc',
      idValueOption: 'valore',
    },
  ];

  public colonne: { [key: string]: Colonna } = {
    anno: {
      title: 'Anno',
      value: 'anno',
      sortable: true,
    },
    settore: {
      title: 'Settore',
      sortable: true,
      value: (record: any) => record?.settori && record?.settori![0]?.titolo,
    },

    dataUltimaConferma: {
      title: 'Ultima Conferma',
      sortable: true,
      value: (record: any) => (record?.dataUltimaConferma ? record?.dataUltimaConferma : ''),
    },
    dataUltimaModifica: {
      title: 'Ultima Modifica',
      value: (record: any) => record?.dataUltimaModifica ?? '',
    },
    dataCreazione: {
      title: 'Data Creazione',
      value: (record: any) => record?.dataCreazione ?? '',
    },

    stato: this.utilityService.getColonnaStato(),

    modifica: {
      type: 'button',
      buttonIcon: (record: any) => (this.isPermessoSoloRead(record) ? 'visibility' : 'edit'),
      title: (record: any) => (this.isPermessoSoloRead(record) ? 'Visualizza' : 'Modifica'),
      buttonId: 'modifica',
      buttonMostraSempre: true,
      sortable: false,
    },
    elimina: {
      ...this.utilityService.getColonnaElimina(),
      nascondiButton: this.nascondiBottoneElimina(),
    },
    elimina_by_developer: {
      type: 'button',
      buttonIcon: 'delete',
      title: 'Elimina by Developer',
      buttonId: 'elimina_by_developer',
      buttonMostraSempre: false,
      sortable: false,
      nascondiButton: (record: any) => !this.isDeveloper || record.stato === 'BOZZA',
    },
  };
  get isDeveloper() {
    return !!this.utenteService.infoUtente?.isDeveloper;
  }
  constructor(
    private readonly settoriService: SettoriService,
    public dialog: MatDialog,
    private readonly utilityService: UtilityService,
    private readonly doppiaMaterialitaService: DoppiaMaterialitaService,
    private readonly aziendaService: AziendaService,
    public utenteService: UtenteService,
    private readonly licenzeService: LicenzeService,
  ) {}

  ngAfterViewInit(): void {
    this._subAziendaCorrente = this.aziendaService.aziendaCorrente.subscribe((azienda) => {
      let cambiato = false;

      if (this.azienda && azienda && this.azienda.id !== azienda.id) {
        cambiato = true;
      }

      this.azienda = azienda;

      if (cambiato) {
        this.tabella?.caricaDati();
      }
    });
  }

  ngOnDestroy(): void {
    this._subAziendaCorrente?.unsubscribe();
  }

  nascondiBottoneElimina() {
    if (this.utenteService.infoUtente?.isDeveloper) {
      return false;
    }
    if (this.utenteService.isPermessoAttivo('DELETE_DOPPIA_MATERIALITA')) {
      return false;
    }
    return true; //nascondere
  }

  isPermessoSoloRead(record: any) {
    const isEdit = this.utenteService.isPermessoAttivo('EDIT_DOPPIA_MATERIALITA');
    const isCreate = this.utenteService.isPermessoAttivo('CREATE_DOPPIA_MATERIALITA');
    const statoArchiviato = record.stato === 'ARCHIVIATO';
    return (!isEdit && !isCreate) || statoArchiviato;
  }

  creaNuovo(record?: any) {
    if (!record) {
      this.spinnerOver.show();
      this.licenzeService.getUserHasLicense('Materialita').subscribe({
        next: (esito) => {
          this.spinnerOver.hide();
          this.apriDialogCreaDoppiaMaterialita(record);
        },
        error: (err) => {
          this.spinnerOver.hide();
          this.utilityService.openDialog({
            titolo: 'Attenzione',
            descrizione: err?.error?.message,
            bottoni: [{ nome_btn: 'Ok' }],
          });
        },
      });
    } else {
      this.apriDialogCreaDoppiaMaterialita(record);
    }
  }

  private apriDialogCreaDoppiaMaterialita(record: any) {
    const dialogCreaAmbito = this.dialog.open(DialogCreaDoppiaMaterialitaComponent, {
      data: {
        doppiaMaterialita: record,
      },
      panelClass: ['dialog-container'],
      disableClose: false,
      width: '80%',
      height: '99%',
      autoFocus: false,
    });

    dialogCreaAmbito.afterClosed().subscribe((result) => {
      this.tabella.caricaDati();
    });
  }

  public fnCaricamentoDati: FnCaricamentoDati = (
    page: number,
    pageSize: number,
    ricerca?: string,
    filters?: Filters[],
    sortBy?: SortBy[],
  ) => {
    return this.doppiaMaterialitaService.getDoppiaMaterialita(
      page,
      pageSize,
      ricerca,
      filters,
      sortBy,
    );
  };

  public bottoniListaClick(event: BottoniListaEvent) {
    switch (event.id) {
      case 'modifica':
        this.creaNuovo(event.data);
        break;
      case 'elimina':
      case 'archivia':
      case 'disarchivia':
        this._elimina(event.data, false);
        break;
      case 'elimina_by_developer':
        this._elimina(event.data, true);
        break;
      default:
        console.warn('ATTENZIONE: bottone ' + event.id + ' non riconosciuto');
    }
  }

  /**
   * Gestisce l'eliminazione di un modulo tramite il service `utilityService`.
   * Mostra lo spinner durante l'operazione e aggiorna la tabella al termine.
   *
   * @async
   * @param {any} modulo - L'oggetto modulo da eliminare. Deve contenere almeno l'ID e lo stato.
   *
   */
  async _elimina(modulo: any, deleteByDeveloper: boolean) {
    this.spinnerOver.show();

    const success = await this.utilityService.eliminaModulo(
      modulo,
      this.doppiaMaterialitaService,
      deleteByDeveloper,
    );

    this.spinnerOver.hide();

    if (success) {
      this.tabella.caricaDati();
    } else {
      console.log('Operazione annullata o non riuscita');
    }
  }
}
